import { useEffect, useRef, useState } from 'react';
import { Upload, Wand2, Search, Plus, RefreshCw } from 'lucide-react';
import { useEmployees, Employee, EmployeeStatus } from '../../hooks/useEmployees';
import { Card, DataTable, Avatar } from '../../components/ui';
import type { Column } from '../../components/ui';
import Button from '../../components/Button';
import EmployeeFormModal from '../../components/admin/EmployeeFormModal';

const STATUS_STYLE: Record<EmployeeStatus, string> = {
  active:     'bg-permus-successLight text-permus-success',
  inactive:   'bg-gray-100 text-gray-500',
  on_leave:   'bg-amber-100 text-amber-700',
  terminated: 'bg-red-100 text-red-700',
};
const STATUS_LABEL: Record<EmployeeStatus, string> = {
  active: 'Active', inactive: 'Inactive', on_leave: 'On Leave', terminated: 'Terminated',
};

const nameOf  = (e: Employee) => e.name ?? e.user?.name ?? '—';
const emailOf = (e: Employee) => e.email ?? e.user?.email ?? '';

const columns: Column<Employee>[] = [
  {
    key: 'name', header: 'Employee', sortValue: e => nameOf(e),
    render: e => (
      <div className="flex items-center gap-2.5">
        <Avatar name={nameOf(e)} size="sm" />
        <div>
          <div className="font-medium text-navy">{nameOf(e)}</div>
          <div className="text-xs text-gray-400">
            {emailOf(e) || (e.user ? '' : <span className="text-gray-300">No portal user</span>)}
          </div>
        </div>
      </div>
    ),
  },
  { key: 'employeeId', header: 'Emp ID', sortValue: e => e.employeeId, render: e => <span className="font-mono text-xs">{e.employeeId}</span> },
  { key: 'designation', header: 'Designation', sortValue: e => e.designation ?? '', render: e => e.designation ?? '—' },
  { key: 'department', header: 'Department', sortValue: e => e.department?.name ?? '', render: e => e.department?.name ?? <span className="text-gray-300">Unlinked</span> },
  { key: 'manager', header: 'Manager', render: e => e.manager?.user?.name ?? '—' },
  {
    key: 'status', header: 'Status', sortValue: e => e.status,
    render: e => (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[e.status] ?? 'bg-gray-100 text-gray-500'}`}>
        {STATUS_LABEL[e.status] ?? e.status}
      </span>
    ),
  },
];

export default function Employees() {
  const {
    employees, total, loading, search, setSearch,
    importCsv, normalizeDepartments, saveEmployee, syncM365, getSyncStatus,
  } = useEmployees();
  const fileRef = useRef<HTMLInputElement>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);

  const loadSyncStatus = async () => {
    const s = await getSyncStatus();
    const fin = s?.lastResult?.finishedAt;
    setLastSync(fin ? new Date(fin).toLocaleString() : null);
  };
  useEffect(() => { loadSyncStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) importCsv(f);
    e.target.value = '';
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit   = (emp: Employee) => { setEditing(emp); setModalOpen(true); };

  const onSync = async () => {
    setSyncing(true);
    await syncM365();
    await loadSyncStatus();
    setSyncing(false);
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy">Employees</h1>
          <p className="text-sm text-gray-500">
            {total} records in the employee master
            {lastSync && <span className="text-gray-400"> · last M365 sync {lastSync}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={normalizeDepartments}><Wand2 size={15} /> Normalize departments</Button>
          <Button variant="ghost" onClick={onSync} disabled={syncing}>
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync from M365'}
          </Button>
          <Button variant="secondary" onClick={() => fileRef.current?.click()}><Upload size={15} /> Import CSV</Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
          <Button onClick={openCreate}><Plus size={15} /> New Employee</Button>
        </div>
      </div>

      <Card flush>
        <div className="p-3 border-b border-gray-100">
          <div className="relative max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, ID…"
              className="w-full text-sm rounded-lg border border-gray-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
          </div>
        </div>
        <DataTable
          columns={columns}
          data={employees}
          rowKey={e => e.id}
          onRowClick={openEdit}
          loading={loading}
          pageSize={25}
          emptyTitle="No employees yet"
          emptyDescription="Add one manually, import a CSV, or run an M365 sync to populate the employee master."
        />
      </Card>

      <p className="text-xs text-gray-400">
        CSV columns: <code>email, employeeId, hireDate, designation, department, costCenter, employmentType, location, managerEmail, status</code>
      </p>

      <EmployeeFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        employee={editing}
        employees={employees}
        onSave={saveEmployee}
      />
    </div>
  );
}
